import type {
  MotionEditorJobListItem,
  MotionEditorJobResponse,
} from '@cartwhl/common/api/postSceneObjectMotionEdit';

import type {
  KeyPoseGenerationHistoryEntry,
  KeyPoseGenerationHistoryStatus,
  KeyPoseGenerationPhase,
} from './types';

export function getGenerationPhaseForJobStatus(
  status: MotionEditorJobResponse['status'],
): KeyPoseGenerationPhase {
  return status === 'QUEUED' ? 'queued' : 'regenerating';
}

export function getGenerationPhaseForHistoryRecovery(
  status: KeyPoseGenerationHistoryStatus,
): KeyPoseGenerationPhase {
  return status === 'queued' ? 'queued' : 'regenerating';
}

export function getGenerationHistoryStatusForJob(
  job: MotionEditorJobResponse,
  currentJobID?: string,
): KeyPoseGenerationHistoryStatus {
  switch (job.status) {
    case 'QUEUED':
      return 'queued';
    case 'GENERATING':
      return 'regenerating';
    case 'COMPLETED':
      return job.jobID === currentJobID ? 'applied' : 'generated';
    case 'FAILED':
      return 'failed';
    case 'CANCELLED':
      return 'cancelled';
  }
}

export function isRecoverableGenerationHistoryStatus(
  status: KeyPoseGenerationHistoryStatus,
) {
  return status === 'queued' || status === 'regenerating';
}

export function sortGenerationHistoryEntries(
  entries: KeyPoseGenerationHistoryEntry[],
) {
  return [...entries].sort(
    (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
  );
}

export function mapMotionEditJobsToHistoryEntries({
  currentJobID,
  jobs,
  motionID,
  referenceName,
  sceneID,
  timelineIndex,
}: {
  currentJobID?: string;
  jobs: MotionEditorJobListItem[];
  motionID: string;
  referenceName: string;
  sceneID: string;
  timelineIndex: number;
}) {
  return jobs.map((job, index) =>
    createGenerationHistoryEntryFromJob({
      currentJobID,
      isBaseline: index === jobs.length - 1,
      job,
      motionID,
      referenceName,
      sceneID,
      timelineIndex,
    }),
  );
}

export function createGenerationHistoryEntryFromJob({
  currentJobID,
  isBaseline = false,
  job,
  motionID,
  referenceName,
  sceneID,
  timelineIndex,
}: {
  currentJobID?: string;
  isBaseline?: boolean;
  job: MotionEditorJobListItem | MotionEditorJobResponse;
  motionID: string;
  referenceName: string;
  sceneID: string;
  timelineIndex: number;
}): KeyPoseGenerationHistoryEntry {
  const updatedAt = job.updatedAt ?? new Date().toISOString();
  const entry: KeyPoseGenerationHistoryEntry = {
    jobID: job.jobID,
    sceneID,
    referenceName,
    timelineIndex,
    motionID,
    operation: job.operation,
    status: getGenerationHistoryStatusForJob(job, currentJobID),
    createdAt: job.createdAt,
    updatedAt,
  };

  if (isBaseline) {
    entry.isBaseline = true;
  }
  if (job.completedAt) {
    entry.completedAt = job.completedAt;
  }
  if (job.outputBvhURL) {
    entry.outputBvhURL = job.outputBvhURL;
  }
  if (job.thumbnailUrl) {
    entry.thumbnailUrl = job.thumbnailUrl;
  }
  if (job.errorMessage) {
    entry.errorMessage = job.errorMessage;
  }
  if (job.prompt) {
    entry.prompt = job.prompt;
  }
  if (job.userKeyPoseCount !== undefined) {
    entry.userKeyPoseCount = job.userKeyPoseCount;
  }

  return entry;
}
